import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Button,
  Paper,
  CircularProgress,
  Divider,
  Grid,
  Alert,
  AlertTitle,
  Tooltip,
  Stack,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import { header } from 'utils/auth';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 0,
  borderRadius: 0,
  overflow: 'hidden',
  width: '650px',
  maxWidth: '95vw',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1400, // Higher than MUI Dialog default z-index (1300)
};

export type DownloadStatus = 'idle' | 'downloading' | 'success' | 'error';

interface DownloadedFile {
  id: string;
  fileName: string;
  fileSize: string;
  fileType: string;
  objectUrl: string;
  dateAdded: Date;
}

interface DownloadModalMAIQProps {
  open: boolean;
  onClose: () => void;
  downloadUrl: string;
  title: string;
  fileName?: string; // Add optional fileName prop
  customStyle?: React.CSSProperties;
}

interface ErrorResponseData {
  message?: string;
  statusCode?: number;
  status?: string;
  error?: string;
  path?: string;
  timestamp?: string;
}

const DownloadModalMAIQ: React.FC<DownloadModalMAIQProps> = ({
  open,
  onClose,
  downloadUrl,
  title,
  fileName, // Use fileName prop if provided
  customStyle = {},
}) => {
  const theme = useTheme();
  const [status, setStatus] = useState<DownloadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<ErrorResponseData | null>(
    null,
  );
  const [fileSize, setFileSize] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [fileObjectUrl, setFileObjectUrl] = useState<string>('');
  const [showHelp, setShowHelp] = useState(false);
  const [downloadedFiles, setDownloadedFiles] = useState<DownloadedFile[]>([]);
  const [showDownloadGrid, setShowDownloadGrid] = useState(false);
  const [fileNameState, setFileName] = useState<string>(''); // Renamed to avoid conflict

  // Reference to store blob data
  const blobRef = useRef<Blob | null>(null);


  // Function to check if URL is a blob URL
  const isBlobUrl = (url: string): boolean => {
    return url.startsWith('blob:');
  };

  // Function to handle blob URL downloads (from ImportExportMAIQ)
  const handleBlobDownload = () => {
    try {
      console.log('📦 Starting blob download from URL:', downloadUrl);
      
      // Fetch the blob from the blob URL
      fetch(downloadUrl)
        .then(response => {
          console.log('📦 Fetch response status:', response.status);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          // Store blob reference
          blobRef.current = blob;
          
          // Set file info
          setFileType(blob.type || 'application/octet-stream');
          setFileSize(formatFileSize(blob.size));
          
          // Use provided fileName or generate one
          let finalFileName = fileName || 'exported_file';
          
          if (!finalFileName.includes('.')) {
            // Add extension based on blob type
            const extension = getDefaultExtension(blob.type);
            if (extension) {
              finalFileName += `.${extension}`;
            } else {
              finalFileName += '.zip'; // Default to zip for exports
            }
          }
          
          setFileName(finalFileName);
          
          // Create object URL for download
          const objectUrl = URL.createObjectURL(blob);
          setFileObjectUrl(objectUrl);
          
          // Simulate progress for better UX
          setProgress(50);
          setTimeout(() => setProgress(100), 200);
          
          // Trigger download without appending to document to avoid DOM XSS
          const link = document.createElement('a');
          link.setAttribute('href', objectUrl);
          link.setAttribute('download', finalFileName);
          link.style.display = 'none';
          link.click();
          
          // Update status
          setStatus('success');
          
          // Add to downloaded files list
          const newFile: DownloadedFile = {
            id: Date.now().toString(),
            fileName: finalFileName,
            fileSize: formatFileSize(blob.size),
            fileType: blob.type || 'application/octet-stream',
            objectUrl: objectUrl,
            dateAdded: new Date(),
          };
          
          setDownloadedFiles(prev => [...prev, newFile]);
          
          console.log('✅ Blob download completed successfully');
        })
        .catch(error => {
          console.error('❌ Blob download error:', error);
          setError('Failed to download file');
          setErrorDetails({
            message: error.message,
            status: 'error',
            error: error.toString(),
          });
          setStatus('error');
        });
    } catch (error) {
      console.error('❌ Blob download error:', error);
      setError('Failed to download file');
      setErrorDetails({
        message: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        error: error instanceof Error ? error.toString() : 'Unknown error',
      });
      setStatus('error');
    }
  };

  // Function to download the file
  const handleDownload = () => {
    setStatus('downloading');
    setProgress(10);
    setError(null);
    setErrorDetails(null);

    // Revoke previous object URL if exists
    if (fileObjectUrl) {
      window.URL.revokeObjectURL(fileObjectUrl);
      setFileObjectUrl('');
    }

    // Check if downloadUrl is empty or invalid
    if (!downloadUrl || downloadUrl.trim() === '') {
      console.error('❌ Download URL is empty');
      setError('Download URL is not available. Please try exporting again.');
      setStatus('error');
      return;
    }

    // Check if it's a blob URL (from ImportExportMAIQ)
    if (isBlobUrl(downloadUrl)) {
      console.log('📦 Handling blob URL download:', downloadUrl);
      handleBlobDownload();
      return;
    }

    // Get the authorization token
    const authToken = header();

    // Use XMLHttpRequest to get better access to headers
    const xhr = new XMLHttpRequest();
    xhr.open('GET', downloadUrl, true);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Authorization', authToken);

    // Track download progress
    xhr.onprogress = event => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        setProgress(percentComplete);
      }
    };

    // Handle successful response
    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        const blob = this.response;

        // Get response headers
        const contentType = xhr.getResponseHeader('content-type') || '';
        const contentDisposition = xhr.getResponseHeader('content-disposition') || xhr.getResponseHeader('Content-Disposition');

        // Log headers for debugging
        console.log('Content-Type:', contentType);
        console.log('Content-Disposition:', contentDisposition);

        // Set file info
        setFileType(contentType);
        setFileSize(formatFileSize(blob.size));

        // Extract filename using multiple approaches
        let extractedFilename = '';

        // Try from Content-Disposition header first
        if (contentDisposition) {
          // Try multiple regex patterns to extract filename
          let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";\n]+)['"]?/i);
          
          if (!filenameMatch) {
            // Fallback pattern for different formats
            filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
          }
          
          if (filenameMatch && filenameMatch[1]) {
            extractedFilename = filenameMatch[1].trim().replace(/['"]/g, '');
            console.log(
              'Filename from Content-Disposition:',
              extractedFilename,
            );
          }
        }

        // If no filename from header, try from URL
        if (!extractedFilename) {
          const urlParts = downloadUrl.split('/');
          let lastPart = urlParts[urlParts.length - 1].split('?')[0];

          if (lastPart) {
            extractedFilename = lastPart;
            console.log('Filename from URL:', extractedFilename);
          }
        }

        // Use provided fileName prop if available, otherwise use extracted filename
        if (fileName) {
          extractedFilename = fileName;
          console.log('Using provided fileName:', extractedFilename);
        } else {
          // Set default if nothing found
          if (!extractedFilename) {
            extractedFilename = 'downloaded_file';
          }

          // Add extension if needed
          if (!extractedFilename.includes('.') && contentType) {
            const extension = getDefaultExtension(contentType);
            if (extension) {
              extractedFilename += `.${extension}`;
            }
          }
        }

        console.log('Final filename:', extractedFilename);
        setFileName(extractedFilename);

        // Create object URL from blob
        const url = window.URL.createObjectURL(blob);
        setFileObjectUrl(url);

        // Add to downloaded files list
        const newFile: DownloadedFile = {
          id: generateUniqueId(),
          fileName: extractedFilename,
          fileSize: formatFileSize(blob.size),
          fileType: contentType,
          objectUrl: url,
          dateAdded: new Date(),
        };

        setDownloadedFiles(prev => [newFile, ...prev]);

        // Direct browser download
        triggerBrowserDownload(url, extractedFilename);

        setProgress(100);
        setStatus('success');
      } else {
        handleXhrError(this);
      }
    };

    // Handle network errors
    xhr.onerror = function () {
      handleXhrError(this);
    };

    // Start the request
    xhr.send();
  };

  // Helper function to handle XHR errors
  const handleXhrError = (xhr: XMLHttpRequest) => {
    console.error('XHR Error:', xhr.status, xhr.statusText);

    // Try to parse error response if possible
    let errorMessage = `Error ${xhr.status}: ${xhr.statusText}`;
    let errorDetails: ErrorResponseData | null = null;

    try {
      // Handle blob response for errors
      if (xhr.responseType === 'blob' && xhr.response) {
        // Convert blob to text to parse JSON error
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const responseText = reader.result as string;
            const parsedError = JSON.parse(responseText) as ErrorResponseData;
            const extractedMessage =
              parsedError?.message || parsedError?.error || 'Unknown error';
            const finalErrorMessage = `${xhr.status} ${xhr.statusText}: ${extractedMessage}`;

            setError(finalErrorMessage);
            setErrorDetails(parsedError);
            setStatus('error');
          } catch (parseError) {
            console.error('Failed to parse blob error response:', parseError);
            setError(errorMessage);
            setStatus('error');
          }
        };
        reader.onerror = () => {
          console.error('Failed to read blob response');
          setError(errorMessage);
          setStatus('error');
        };
        reader.readAsText(xhr.response);
        return; // Exit early, will set error in reader.onload
      }

      // Handle text response for errors
      if (xhr.responseType === '' || xhr.responseType === 'text') {
        errorDetails = JSON.parse(xhr.responseText) as ErrorResponseData;
        const extractedMessage =
          errorDetails?.message || errorDetails?.error || 'Unknown error';
        errorMessage = `${xhr.status} ${xhr.statusText}: ${extractedMessage}`;
      }
    } catch (e) {
      console.error('Failed to parse error response:', e);
    }

    setError(errorMessage);
    setErrorDetails(errorDetails);
    setStatus('error');
  };

  // Trigger browser download (without appending to document to avoid DOM XSS)
  const triggerBrowserDownload = (url: string, fileName: string) => {
    try {
      const a = document.createElement('a');
      a.style.display = 'none';
      a.setAttribute('href', url);
      a.setAttribute('download', fileName);
      a.click();
      console.log('File download initiated:', fileName);
    } catch (err) {
      console.error('Download failed (primary method):', err);
      // Intentionally no window.open fallback to prevent open-redirect (CWE-601);
      // primary <a> click works in all supported browsers.
    }
  };

  // Generate a unique ID for list items
  const generateUniqueId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  // Function to download a specific file from the list
  const handleDownloadFile = (file: DownloadedFile) => {
    triggerBrowserDownload(file.objectUrl, file.fileName);
  };

  // Function to remove a file from the list
  const handleRemoveFile = (id: string) => {
    setDownloadedFiles(prev => {
      // Find the file to remove
      const fileToRemove = prev.find(file => file.id === id);

      // Revoke object URL to prevent memory leaks
      if (fileToRemove?.objectUrl) {
        window.URL.revokeObjectURL(fileToRemove.objectUrl);
      }

      // Return new array without the removed file
      return prev.filter(file => file.id !== id);
    });
  };

  // Function to open browser's downloads folder
  const openDownloadsFolder = () => {
    // Simply show the instructions for accessing downloads
    showManualInstructions();
  };

  // Show a dialog with manual instructions for finding downloads
  const showManualInstructions = () => {
    // Create a more detailed alert with browser-specific information
    let message = 'To access your Downloads:\n\n';

    if (navigator.userAgent.includes('Chrome')) {
      message += '• Press Ctrl+J (Windows/Linux) or Cmd+Shift+J (Mac)\n';
      message += '• Or click the menu button (⋮) > Downloads';
    } else if (navigator.userAgent.includes('Firefox')) {
      message += '• Press Ctrl+J (Windows/Linux) or Cmd+J (Mac)\n';
      message += '• Or click the menu button (≡) > Downloads';
    } else if (navigator.userAgent.includes('Safari')) {
      message += '• Click Safari menu > Downloads\n';
      message += '• Or press Option+Cmd+L';
    } else if (navigator.userAgent.includes('Edge')) {
      message += '• Press Ctrl+J\n';
      message += '• Or click the menu button (⋯) > Downloads';
    } else {
      message += '• Check your browser menu for a Downloads option\n';
      message += '• Try pressing Ctrl+J or Cmd+J';
    }

    alert(message);
  };

  // This function attempts to locate and open the downloads folder on the OS
  const tryOpenOSDownloadsFolder = () => {
    // Simply show manual instructions instead
    showManualInstructions();
  };

  // Helper function to determine file extension from content type
  const getDefaultExtension = (contentType: string): string => {
    const contentTypeMap: Record<string, string> = {
      'application/pdf': 'pdf',
      'application/json': 'json',
      'application/xml': 'xml',
      'application/zip': 'zip',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        'xlsx',
      'application/vnd.ms-powerpoint': 'ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'pptx',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'docx',
      'text/plain': 'txt',
      'text/html': 'html',
      'text/css': 'css',
      'text/javascript': 'js',
      'text/csv': 'csv',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/svg+xml': 'svg',
      'audio/mpeg': 'mp3',
      'audio/ogg': 'ogg',
      'audio/wav': 'wav',
      'video/mp4': 'mp4',
      'video/mpeg': 'mpeg',
      'video/webm': 'webm',
    };

    // Check if content type has parameters (e.g., charset) and strip them
    const baseContentType = contentType.split(';')[0].trim().toLowerCase();

    return contentTypeMap[baseContentType] || '';
  };

  // Create a new section with browser-specific keyboard shortcuts
  const renderDownloadsHelp = () => {
    let shortcut = '';
    let menuPath = '';

    if (navigator.userAgent.includes('Chrome')) {
      shortcut = 'Ctrl+J (Windows/Linux) or Cmd+Shift+J (Mac)';
      menuPath = 'Menu (⋮) → Downloads';
    } else if (navigator.userAgent.includes('Firefox')) {
      shortcut = 'Ctrl+J (Windows/Linux) or Cmd+J (Mac)';
      menuPath = 'Menu (≡) → Downloads';
    } else if (navigator.userAgent.includes('Safari')) {
      shortcut = 'Option+Cmd+L';
      menuPath = 'Safari menu → Downloads';
    } else if (navigator.userAgent.includes('Edge')) {
      shortcut = 'Ctrl+J';
      menuPath = 'Menu (⋯) → Downloads';
    } else {
      shortcut = 'Usually Ctrl+J or Cmd+J';
      menuPath = 'Check browser menu for Downloads option';
    }

    return (
      <Box
        sx={{
          mt: 2,
          mb: 2,
          p: 2,
          bgcolor: theme.palette.action.hover,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}
        >
          How to Access Your Downloads:
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Keyboard shortcut:</strong> {shortcut}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Browser menu:</strong> {menuPath}
        </Typography>
      </Box>
    );
  };

  // Format file size in human-readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Extract file extension from filename
  const getFileExtension = (name: string): string => {
    const parts = name.split('.');
    return parts.length > 1 ? parts.pop()?.toUpperCase() || '' : '';
  };

  // Format date to readable format
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Auto-start download when modal opens
  useEffect(() => {
    if (open && status === 'idle') {
      handleDownload();
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStatus('idle');
        setProgress(0);
        setError(null);
        setErrorDetails(null);
        setFileSize('');
        setFileType('');
        setShowHelp(false);
        setShowDownloadGrid(false);
        setFileName('');

        // Don't clear downloadedFiles or revoke objectUrls when closing
        // They should persist between openings
      }, 300);
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup all object URLs when component unmounts
      downloadedFiles.forEach(file => {
        if (file.objectUrl) {
          window.URL.revokeObjectURL(file.objectUrl);
        }
      });
    };
  }, [downloadedFiles]);

  // Render file info box
  const renderFileInfo = () => {
    const isFileReady = status === 'success' && fileObjectUrl;

    return (
      <Box
        sx={{
          my: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: theme.palette.action.hover,
        }}
      >
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Box sx={{ p: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <InfoIcon fontSize="small" />
                File Information
              </Typography>

              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    File Name:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" fontWeight={500}>
                    {fileNameState || 'Downloading...'}
                  </Typography>
                </Grid>

                {fileSize && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Size:
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2">{fileSize}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render downloaded files grid modal
  const renderDownloadedFilesGrid = () => {
    return (
      <Modal
        open={showDownloadGrid}
        onClose={() => setShowDownloadGrid(false)}
        disablePortal
      >
        <Box
          sx={{
            ...modalStyle,
            width: '800px',
          }}
        >
          <Paper
            elevation={0}
            sx={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" fontWeight="600">
                Downloaded Files
              </Typography>
              <IconButton
                onClick={() => setShowDownloadGrid(false)}
                sx={{
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                p: 3,
                flex: '1 1 auto',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {downloadedFiles.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: '200px',
                    color: 'text.secondary',
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6">No Downloads Yet</Typography>
                  <Typography variant="body2">
                    Downloaded files will appear here
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table
                    sx={{ minWidth: 650 }}
                    aria-label="downloaded files table"
                  >
                    <TableHead>
                      <TableRow
                        sx={{ backgroundColor: theme.palette.action.hover }}
                      >
                        <TableCell>File Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Date Added</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {downloadedFiles.map(file => (
                        <TableRow
                          key={file.id}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <InsertDriveFileIcon sx={{ color: theme.palette.text.primary }} />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {file.fileName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={file.fileType}>
                              <Typography variant="body2">
                                {getFileExtension(file.fileName) || 'Unknown'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {file.fileSize}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(file.dateAdded)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 1,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadFile(file)}
                                color="primary"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(file.id)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                backgroundColor: theme.palette.action.hover,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {downloadedFiles.length} file
                {downloadedFiles.length !== 1 ? 's' : ''} available
              </Typography>
              <Button
                variant="text"
                onClick={openDownloadsFolder}
                startIcon={<FolderOpenIcon />}
                sx={{ color: theme.palette.text.primary }}
              >
                Open Downloads Folder
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>
    );
  };

  const renderContent = () => {
    switch (status) {
      case 'downloading':
        return (
          <>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 3, color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.primary }}>
                Downloading File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please wait while your file is being prepared...
              </Typography>

              {renderFileInfo()}

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 1,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {`${Math.round(progress)}%`}
              </Typography>
            </Box>
          </>
        );

      case 'success':
        return (
          <>
            <Box sx={{ p: 4, pb: 2 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 60, color: 'success.main', mb: 2 }}
                />
                <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.primary }}>
                  Download Complete
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Your file has been downloaded successfully.
                </Typography>
              </Box>

              {renderFileInfo()}

              {renderDownloadsHelp()}
            </Box>

            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                p: 2,
                backgroundColor: theme.palette.action.hover,
              }}
            >
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                fullWidth
                size="large"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  py: 1.2,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                Download Again
              </Button>
            </Box>
          </>
        );

      case 'error':
        return (
          <>
            <Box sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <ErrorOutlineIcon
                  sx={{ fontSize: 60, color: 'error.main', mb: 2 }}
                />
                <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.primary }}>
                  Download Failed
                </Typography>
              </Box>

              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <AlertTitle>Error</AlertTitle>
                {error || 'An error occurred during download'}
              </Alert>

              {errorDetails && (
                <Box sx={{ mb: 3, textAlign: 'left' }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Error Details:
                  </Typography>
                  {errorDetails.statusCode && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status Code:</strong> {errorDetails.statusCode}
                    </Typography>
                  )}
                  {errorDetails.path && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Path:</strong> {errorDetails.path}
                    </Typography>
                  )}
                  {errorDetails.timestamp && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Time:</strong>{' '}
                      {new Date(errorDetails.timestamp).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              )}

              {renderFileInfo()}

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          </>
        );

      default:
        return (
          <>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2, color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.primary }}>
                Preparing Download
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait...
              </Typography>

              {renderFileInfo()}
            </Box>
          </>
        );
    }
  };

  return (
    <>
      <Modal 
        open={open} 
        onClose={onClose} 
        disablePortal
        sx={{ zIndex: 1400 }} // Higher than MUI Dialog default z-index (1300)
      >
        <Box sx={{ ...modalStyle, ...customStyle }}>
          <Paper elevation={0}>
            {/* Header */}
            <Box
              sx={{
                p: 2,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" fontWeight="600">
                {title}
              </Typography>
              <IconButton
                onClick={onClose}
                sx={{
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            {renderContent()}
          </Paper>
        </Box>
      </Modal>

      {/* Nested modal for showing the grid of downloaded files */}
      {renderDownloadedFilesGrid()}
    </>
  );
};

export default DownloadModalMAIQ;
